import React, { useMemo } from "react"
import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, from } from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"

interface AuthApolloProviderProps {
  uri: string
  children: React.ReactNode
}

function createApolloClient(uri: string): ApolloClient<any> {
  const httpLink = new HttpLink({ uri })

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors)
      graphQLErrors.forEach(({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      )
    if (networkError) console.log(`[Network error]: ${networkError}`)
  })

  const authLink = setContext(async (_, { headers }) => {
    // Always get the latest token from localStorage (better-auth pattern)
    const betterAuthToken = localStorage.getItem("bearer_token")

    let newHeaders = {
      ...headers,
      "X-Auth-Type": "better-auth", // Critical: Tell backend we're using better-auth
    }
    if (betterAuthToken) {
      newHeaders.authorization = `Bearer ${betterAuthToken}`
    }
    return {
      headers: newHeaders,
    }
  })

  return new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            // Feature toggles - cache for 5 minutes
            getFeatureToggleDefinitions: {
              merge: false,
            },
            getOrganizationFeatureToggles: {
              keyArgs: ["organizationId"],
              merge: false,
            },
            getGlobalFeatureToggles: {
              merge: false,
            },
            getOrganizationFeatureOverrides: {
              keyArgs: ["organizationId"],
              merge: false,
            },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
        errorPolicy: "all",
      },
      query: {
        fetchPolicy: "cache-and-network",
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
    },
  })
}

export default function AuthApolloProvider({ uri, children }: AuthApolloProviderProps) {
  const client = useMemo(() => createApolloClient(uri), [uri])

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}
